// Appels d'API des QUESTIONNAIRES D'ÉTUDE (062) : envoi, réponses, résultats.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';

export type QuestionType = 'ouverte' | 'echelle' | 'choix_unique' | 'choix_multiple' | 'oui_non';

export interface Recipient {
  id: string;
  name: string;
  email: string | null;
  personId: string | null;
  sentAt: string;
  answeredAt: string | null;
  status: 'envoye' | 'repondu';
}

export interface QuestionResult {
  key: string;
  section: string;
  text: string;
  type: QuestionType;
  answered: number;
  counts?: { label: string; count: number }[];
  average?: number | null;
  min?: number;
  max?: number;
  texts?: string[];
}

export interface PublicQuestion {
  key: string;
  section: string;
  text: string;
  type: QuestionType;
  options: string[];
  min?: number;
  max?: number;
}

export interface PublicQuestionnaireView {
  /** `null` : lien commun, personne n'y est nommé. */
  name: string | null;
  title: string;
  organizer: string | null;
  organization: string;
  intro: string;
  closing: string;
  answered: boolean;
  /** Réponses enregistrées sans identité : elles ne se retrouvent plus ensuite. */
  anonymous: boolean;
  closed: boolean;
  questions: PublicQuestion[];
}

export interface WaveScores {
  nps: { value: number; n: number; promoters: number; passives: number; detractors: number } | null;
  sus: { value: number; n: number; excluded: number } | null;
  pillars: { name: string; axis: string; value: number; n: number }[];
}

export interface QuestionnaireResults {
  sent: number;
  respondents: number;
  questions: QuestionResult[];
  /** Présents sur une vague de baromètre seulement. */
  masked?: boolean;
  min?: number;
  fragile?: boolean;
  closed?: boolean;
  scores?: WaveScores;
}

const base = (studyId: string) => `/api/studies/${encodeURIComponent(studyId)}/questionnaire`;

export function useRecipients(studyId: string) {
  return useQuery({
    queryKey: ['questionnaire', studyId] as const,
    queryFn: () => api.get<{ recipients: Recipient[]; questions: number }>(base(studyId)),
  });
}

export function useQuestionnaireResults(studyId: string) {
  return useQuery({
    queryKey: ['questionnaire', studyId, 'results'] as const,
    queryFn: () => api.get<QuestionnaireResults>(`${base(studyId)}/results`),
  });
}

export function useSendQuestionnaire(studyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: { personIds: string[]; message?: string }) =>
      api.post<{ sent: { id: string; name: string; email: string | null; token: string; mailSent: boolean }[]; skipped: { personId: string; reason: string }[] }>(base(studyId), b),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['questionnaire', studyId] }),
  });
}

export function useRemoveRecipient(studyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ deleted: boolean }>(`/api/questionnaire-recipients/${encodeURIComponent(id)}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['questionnaire', studyId] }),
  });
}

export function usePublicStudyQuestionnaire(token: string) {
  return useQuery({
    queryKey: ['public-questionnaire', token] as const,
    queryFn: () => api.get<{ questionnaire: PublicQuestionnaireView }>(`/api/public/questionnaires/${encodeURIComponent(token)}`),
    retry: false,
  });
}

export function useAnswerQuestionnaire(token: string) {
  return useMutation({
    mutationFn: (answers: Record<string, unknown>) =>
      api.post<{ answered: boolean }>(`/api/public/questionnaires/${encodeURIComponent(token)}`, { answers }),
  });
}

// --- Baromètre d'adoption (064) ------------------------------------------------

export interface Wave {
  id: string;
  title: string;
  createdAt: string;
  closedAt: string | null;
  model: string;
  respondents: number;
}

export function useWaves(projectId: string) {
  return useQuery({
    queryKey: ['barometer', projectId] as const,
    queryFn: () => api.get<{ waves: Wave[] }>(`/api/projects/${encodeURIComponent(projectId)}/barometer`),
  });
}

export function useStartWave(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ id: string; token: string }>(`/api/projects/${encodeURIComponent(projectId)}/barometer`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['barometer', projectId] }),
  });
}

export function useRenewWaveLink(studyId: string) {
  return useMutation({
    mutationFn: () => api.post<{ token: string }>(`/api/studies/${encodeURIComponent(studyId)}/barometer/link`, {}),
  });
}

export function useCloseWave(studyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ closed: boolean; measures: number }>(`/api/studies/${encodeURIComponent(studyId)}/barometer/close`, {}),
    onSuccess: () => void qc.invalidateQueries(),
  });
}
